/**
 * The address bar, told the truth.
 *
 * The portal was a single screen that swapped its contents: every page lived
 * at `/`, so the URL never said where you were, Back left the portal entirely
 * instead of undoing your last move, and no page could be linked to or
 * bookmarked. This module is the map between what you are looking at and the
 * path that names it — nothing more. Writing it is `pathOf`; reading it is the
 * route table below, which React Router matches and `routeOf` turns back into a
 * `Route`. Both sides use the same words, so they can never drift apart.
 *
 * The paths are the words the sidebar already uses, not internal state names:
 * `/tickets`, not `/mywork?tab=tickets`. Someone reading the address bar
 * should recognise the page they are on.
 */
import type { Section, DocsTab } from '@/components/FloatingDock';
import type { MyWorkTab } from '@/hooks/useMyWorkTab';

export interface Route {
  section: Section;
  /** Which "My work" page — only meaningful when section is `mywork`. */
  mwTab?: MyWorkTab;
  /** Which Resources tab; absent means the Resources hub itself. */
  docsTab?: DocsTab;
  /** The open case, when one is open on the Tickets page. A case is a page,
   *  so it gets a path — which is also what makes it linkable and what lets
   *  Back close it. */
  caseId?: string | null;
  /** The open task, when one is open on the Tasks page — the same idea as
   *  `caseId`, one tab over. */
  taskId?: string | null;
}

/** Resources tabs get readable paths — `howwework` is a variable name, not a word. */
const DOCS_PATH: Record<DocsTab, string> = {
  services: 'services',
  documents: 'documents',
  howwework: 'how-we-work',
  trainings: 'trainings',
};

const MW_TABS: MyWorkTab[] = ['tickets', 'tasks', 'notes', 'handover'];

/** Where a given view lives. Always starts with "/". */
export function pathOf(r: Route): string {
  switch (r.section) {
    case 'tools': return '/home';
    /* The four My-work pages are top-level entries in the sidebar, so they are
       top-level paths too: what you clicked is what you read. */
    case 'mywork': {
      const tab = r.mwTab && MW_TABS.includes(r.mwTab) ? r.mwTab : 'tickets';
      if (tab === 'tickets' && r.caseId) return `/tickets/${encodeURIComponent(r.caseId)}`;
      if (tab === 'tasks' && r.taskId) return `/tasks/${encodeURIComponent(r.taskId)}`;
      return `/${tab}`;
    }
    case 'documents': return r.docsTab ? `/resources/${DOCS_PATH[r.docsTab]}` : '/resources';
    case 'logbook': return '/logbook';
    case 'admin': return '/admin';
    case 'notifications': return '/notifications';
    case 'stats': return '/reports';
    case 'planner': return '/planner';
    case 'calendar': return '/calendar';
    case 'inbox': return '/inbox';
    /* France Actes is a section of its own, so it has an address of its own —
       without this every link into it wrote `/home` and a reload lost it. */
    case 'actes': return '/actes';
    default: return '/home';
  }
}

export interface RouteHandle {
  section: Section;
  mwTab?: MyWorkTab;
  docsTab?: DocsTab;
}

/** Every address the portal serves, relative to its root layout. `:caseId` and
 *  `:taskId` are the open case and task. */
export const PORTAL_ROUTES: { path: string; handle: RouteHandle }[] = [
  { path: 'home', handle: { section: 'tools' } },
  ...MW_TABS.map((tab) => ({ path: tab, handle: { section: 'mywork', mwTab: tab } as RouteHandle })),
  { path: 'tickets/:caseId', handle: { section: 'mywork', mwTab: 'tickets' } },
  { path: 'tasks/:taskId', handle: { section: 'mywork', mwTab: 'tasks' } },
  { path: 'resources', handle: { section: 'documents' } },
  ...Object.entries(DOCS_PATH).map(([tab, seg]) => ({
    path: `resources/${seg}`,
    handle: { section: 'documents', docsTab: tab as DocsTab } as RouteHandle,
  })),
  { path: 'logbook', handle: { section: 'logbook' } },
  { path: 'admin', handle: { section: 'admin' } },
  { path: 'notifications', handle: { section: 'notifications' } },
  { path: 'reports', handle: { section: 'stats' } },
  { path: 'planner', handle: { section: 'planner' } },
  { path: 'calendar', handle: { section: 'calendar' } },
  { path: 'inbox', handle: { section: 'inbox' } },
  { path: 'actes', handle: { section: 'actes' } },
];

/** Read the matched routes back into a view, or null when the address names
 *  nothing we serve — `/` on a cold start, or a stale link. */
export function routeOf(matches: { handle?: unknown; params: Record<string, string | undefined> }[]): Route | null {
  for (let i = matches.length - 1; i >= 0; i--) {
    const h = matches[i].handle as RouteHandle | undefined;
    if (!h?.section) continue;
    const { caseId, taskId } = matches[i].params;
    return { section: h.section, mwTab: h.mwTab, docsTab: h.docsTab, caseId: caseId ?? null, taskId: taskId ?? null };
  }
  return null;
}

const KEPT_SECTIONS: Section[] = ['tools', 'documents', 'logbook', 'mywork', 'admin', 'notifications'];

/** Where `/`, or an address that names nothing, lands: the recap e-mail's
 *  `?go=notifications`, else the section this TAB last had open (a reload must
 *  not throw you back to Quick Access), else Quick Access. The other query
 *  parameters travel with it; `go` is spent. */
export function entryPath(search: string, mwTab: MyWorkTab): string {
  const params = new URLSearchParams(search);
  const go = params.get('go');
  params.delete('go');
  let section: Section = 'tools';
  if (go === 'notifications') section = 'notifications';
  else {
    try {
      const kept = sessionStorage.getItem('lexidy-kb-section') as Section | null;
      if (kept && KEPT_SECTIONS.includes(kept)) section = kept;
    } catch { /* no storage to read — the default stands */ }
  }
  const rest = params.toString();
  return pathOf({ section, mwTab }) + (rest ? `?${rest}` : '');
}

/**
 * PAGES THAT LIVE OUTSIDE THE PORTAL'S FRAME, REACHED ONLY BY THEIR LINK.
 *
 * `/visitor-visa` (Gerónimo, 23 Sept 2026) is a landing about the new Visitor
 * Visa process: no sidebar, no top bar, not in any menu. It is still behind
 * the sign-in, and its data behind the server's own access check.
 *
 * Google sign-in sends the browser back to the site's root, so the page asks
 * to be remembered before leaving (`rememberStandaloneReturn`) and is restored
 * on the way back (`standaloneFromReturn`).
 */
export type StandalonePage = 'visitor-visa';
const RETURN_KEY = 'lexidy-return-page';

export function standalonePage(pathname: string): StandalonePage | null {
  const head = pathname.replace(/^\/+|\/+$/g, '').split('/')[0]?.toLowerCase();
  return head === 'visitor-visa' ? 'visitor-visa' : null;
}

/** Before leaving for Google: remember a standalone page to come back to —
 *  with its #hash, so a link to one client's file still opens that file. */
export function rememberStandaloneReturn(): void {
  try {
    if (standalonePage(window.location.pathname)) sessionStorage.setItem(RETURN_KEY, window.location.pathname + window.location.hash);
    else sessionStorage.removeItem(RETURN_KEY);
  } catch { /* private mode: they land on the portal's home instead */ }
}

/** Already on the page (a password sign-in, or Back from Google): the
 *  remembered return is spent, so it cannot pull the person back later. */
export function forgetStandaloneReturn(): void {
  try { sessionStorage.removeItem(RETURN_KEY); } catch { /* nothing to forget */ }
}

/** Back from Google at the root: put the remembered page back in the address
 *  bar and say which it was. Only once — the key is consumed. */
export function standaloneFromReturn(): StandalonePage | null {
  let saved = '';
  try { saved = sessionStorage.getItem(RETURN_KEY) || ''; sessionStorage.removeItem(RETURN_KEY); } catch { return null; }
  const page = standalonePage(saved.split('#')[0]);
  if (!page || !['/', '/home'].includes(window.location.pathname)) return null;
  window.history.replaceState({}, '', saved);
  return page;
}
